import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/auth';
import { LEGAL_VERSIONS } from '@/constants/agreementConstants';

interface SupabaseAuthContextType {
  user: UserProfile | null;
  session: Session | null;
  isLoaded: boolean;
  isSignedIn: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasAcceptedEula: boolean;
  signUp: (email: string, password: string, userData?: { firstName?: string; lastName?: string }) => Promise<{ user: User | null; error: string | null }>;
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null; error: string | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
  getToken: () => Promise<string | null>;
  userRole: string;
}

const SupabaseAuthContext = createContext<SupabaseAuthContextType | null>(null);

export const useAuth = (): SupabaseAuthContextType => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    // Return safe defaults instead of throwing to prevent cascading errors
    // when components render outside provider during initialization
    return {
      user: null,
      session: null,
      isLoaded: false,
      isSignedIn: false,
      isLoading: true,
      isAuthenticated: false,
      hasAcceptedEula: false,
      signUp: async () => ({ user: null, error: 'Auth not initialized' }),
      signIn: async () => ({ user: null, session: null, error: 'Auth not initialized' }),
      signOut: async () => {},
      hasRole: () => false,
      getToken: async () => null,
      userRole: 'viewer',
    };
  }
  return context;
};

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<string>('viewer');
  const [hasAcceptedEula, setHasAcceptedEula] = useState(false);

  // Check EULA acceptance
  const checkEulaAcceptance = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('eula_acceptances')
        .select('id')
        .eq('user_id', userId)
        .eq('eula_version', LEGAL_VERSIONS.EULA)
        .maybeSingle();
      
      setHasAcceptedEula(!!data);
    } catch (error) {
      console.error('Error checking EULA acceptance:', error);
      setHasAcceptedEula(false);
    }
  }, []);

  // Fetch user profile and role from database
  const fetchUserProfile = useCallback(async (userId: string, userEmail?: string) => {
    try {
      // Get profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // Get user roles
      const { data: roles, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (roleError) {
        console.error('Error fetching roles:', roleError);
        setUserRole('viewer');
      } else {
        const userRoles = roles.map(r => r.role);
        if (userRoles.includes('owner')) {
          setUserRole('owner');
        } else if (userRoles.includes('admin')) {
          setUserRole('admin');
        } else if (userRoles.includes('analyst')) {
          setUserRole('analyst');
        } else {
          setUserRole('viewer');
        }
      }

      // Check EULA acceptance
      await checkEulaAcceptance(userId);

      // Create UserProfile object
      if (profile) {
        const userProfileData: UserProfile = {
          id: userId,
          email: userEmail,
          first_name: profile.first_name,
          last_name: profile.last_name,
          avatar_url: profile.avatar_url,
          role: userRole,
          beta_access: profile.beta_access,
          created_at: profile.created_at,
          updated_at: profile.updated_at,
          name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || undefined
        };
        setUserProfile(userProfileData);
      }
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
    }
  }, [userRole, checkEulaAcceptance]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth state changed:', event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setTimeout(() => {
            fetchUserProfile(session.user.id, session.user.email);
          }, 0);
        } else {
          setUserProfile(null);
          setUserRole('viewer');
          setHasAcceptedEula(false);
        }
        
        setIsLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setTimeout(() => {
          fetchUserProfile(session.user.id, session.user.email);
        }, 0);
      }
      
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchUserProfile]);

  const signUp = async (email: string, password: string, userData?: { firstName?: string; lastName?: string }) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: userData?.firstName || '',
          last_name: userData?.lastName || ''
        }
      }
    });

    return { 
      user: data.user, 
      error: error?.message || null 
    };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    return {
      user: data.user,
      session: data.session,
      error: error?.message || null
    };
  };

  const signOut = useCallback(async (): Promise<void> => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }, []);

  const hasRole = useCallback((role: string | string[]): boolean => {
    if (!userRole) {
      return false;
    }
    
    return Array.isArray(role) ? role.includes(userRole) : userRole === role;
  }, [userRole]);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error('Failed to get token:', error);
      return null;
    }
  }, []);

  const contextValue: SupabaseAuthContextType = {
    user: userProfile,
    session,
    isLoaded: !isLoading,
    isSignedIn: !!user,
    isLoading,
    isAuthenticated: !!user,
    hasAcceptedEula,
    signUp,
    signIn,
    signOut,
    hasRole,
    getToken,
    userRole,
  };

  return (
    <SupabaseAuthContext.Provider value={contextValue}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};